import { datadogRum } from '@datadog/browser-rum';
import { LicenseInfo } from '@mui/x-license-pro';
import { MUI_LICENSE_KEY } from 'constants/mui';
import { Suspense, useEffect } from 'react';
import { Route, RouteProps, Routes, useLocation, useSearchParams } from 'react-router-dom';
// components
import { FeaturebaseWidget } from 'components/featurebase-widget/featurebase-widget';
import { HashtagModals } from 'components/hashtag-modals';
import RouteWrapper from 'components/route-wrapper';
import Spinner from 'components/spinner';
import AppHeader from './components/app-header';
// custom hooks
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useSocketListener } from 'hooks/useSocketListener';
import { useAppSelector } from 'redux-store/hooks/useAppSelector';
// api
import { useGetMeQuery } from 'redux-store/api/auth';
import { useGetCompanyQuery } from 'redux-store/api/company';
import { useGetCompanyRolePermissionsMeQuery } from 'redux-store/api/company-roles';
import { useGetGeolocationQuery } from 'redux-store/api/geolocation';
import { useGetProfilePreferenceQuery } from '../redux-store/api/profile-preference';
// config
import { AppPaths, IRoute, baseRoutesConfig, modalRoutesConfig } from './route-config';
// selectors
import { authSelector } from 'redux-store/selector';

import '@draft-js-plugins/emoji/lib/plugin.css';
import '@draft-js-plugins/mention/lib/plugin.css';
import 'dhtmlx-gantt/dhtmlxgantt.css';
import 'draft-js/dist/Draft.css';
import SubscriptionNotificationModal from 'pages/profile/sections/billing-section/modals/subscription-info-modal';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-phone-input-2/lib/high-res.css';

LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

const App = () => {
  const location = useLocation();
  const state = location.state as { backgroundLocation?: Location };
  const { isAuth, companyId } = useAppSelector(authSelector);
  const [searchParams] = useSearchParams();
  LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

  const {
    data: userMe,
    isLoading: isUserMeLoading,
    error: userMeError,
  } = useGetMeQuery(undefined, {
    skip: !isAuth || searchParams.has('entryToken'),
  });

  const {
    data: userPermissions,
    isLoading: isUserPermissionsLoading,
    error: userPermissionsError,
  } = useGetCompanyRolePermissionsMeQuery(undefined, {
    skip: !userMe || !companyId,
  });

  const { isLoading: isCompanyLoading, error: companyError } = useGetCompanyQuery(undefined, {
    skip: !userPermissions?.COMPANY_MANAGEMENT?.canRead,
  });

  useGetGeolocationQuery(undefined, { skip: !userMe });

  const { isLoading: isProfilePreferenceLoading, error: profilePreferenceError } =
    useGetProfilePreferenceQuery(undefined, { skip: !userMe });

  const pending =
    (isUserMeLoading && !userMeError) ||
    (isUserPermissionsLoading && !userPermissionsError) ||
    (isCompanyLoading && !companyError) ||
    (isProfilePreferenceLoading && !profilePreferenceError);

  const createRoutes = (routes: IRoute[]) => {
    return routes.map(
      (
        {
          component: Component,
          accessPermissions,
          role,
          childRoutes,
          index,
          path,
          withWrapper,
          protectedRoute,
          withBackgroundLocation,
        },
        idx
      ) => {
        if ((protectedRoute && !isAuth) || (withBackgroundLocation && !state?.backgroundLocation)) {
          return null;
        }

        const hasAccess =
          !accessPermissions?.length ||
          accessPermissions.some(
            (permissionSubject) => userPermissions?.[permissionSubject]?.canRead
          );

        const hasUpdateAccess =
          !accessPermissions?.length ||
          accessPermissions.some(
            (permissionSubject) => userPermissions?.[permissionSubject]?.canUpdate
          );

        if (path === AppPaths.LAG_TYPE_SECTION && !hasUpdateAccess) {
          return null;
        }

        if (hasAccess) {
          const renderComponent = withWrapper ? (
            <RouteWrapper component={Component} role={role} path={path} />
          ) : Component ? (
            <Component />
          ) : null;

          const routeProps: RouteProps = {
            ...(path && { path }),
            ...(index && { index }),
          };

          return (
            // @ts-ignore
            <Route key={path ?? idx} element={renderComponent} {...routeProps}>
              {childRoutes?.length ? createRoutes(childRoutes) : null}
            </Route>
          );
        }

        return null;
      }
    );
  };

  useSocketListener();
  useBroadcastChannel();

  // Track route changes for Datadog RUM
  useEffect(() => {
    if (!pending && location.pathname) {
      // Normalize pathname to remove dynamic parameters for better grouping
      // e.g., /project-dashboard/123 -> /project-dashboard/:projectId
      const normalizedPath = location.pathname
        .replace(/\/\d+/g, '/:id')
        .replace(/\/[a-f0-9-]{36}/gi, '/:id');

      // Track the view with normalized path for better grouping in Datadog
      datadogRum.startView(normalizedPath);
    }
  }, [location.pathname, pending]);

  if (pending) {
    return <Spinner />;
  }

  return (
    <>
      <FeaturebaseWidget />
      <AppHeader userMe={userMe} />
      <Suspense fallback={<Spinner />}>
        <HashtagModals />
        <Routes location={state?.backgroundLocation || location}>
          {createRoutes(Object.values(baseRoutesConfig))}
        </Routes>
        <Routes>{createRoutes(Object.values(modalRoutesConfig))}</Routes>
      </Suspense>
      <SubscriptionNotificationModal />
    </>
  );
};

export default App;
